import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import DistributorPaymentSupportCase from './DistributorPaymentSupportCase';
import DistributorMerchantFunding from './DistributorMerchantFunding';
import { moneyMinor } from '../../utils/money';
import {
  readPurchaseReceipt,
  purchaseState,
  purchaseCreditMessage,
  samePurchaseQuote,
} from '../distributor/distributorPurchaseReceipt';

const unwrap = (response) => response?.data?.data;
const date = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const safeCount = (value) => Number.isSafeInteger(value) && value >= 0;
const emptyReview = { purchaseId: '', expectedRevision: 0, reason: '', confirmedReconciled: false };

function readRecovery(value) {
  const receipt = readPurchaseReceipt({ data: { data: value } });
  const recovery = value.recovery;
  if (
    receipt.method !== 'ach' ||
    !recovery ||
    !safeCount(recovery.revision) ||
    typeof recovery.blocked !== 'boolean' ||
    typeof recovery.canResolve !== 'boolean' ||
    !(recovery.resolvedAt === null || date(recovery.resolvedAt)) ||
    (recovery.canResolve &&
      (!recovery.blocked ||
        !['returned', 'refund_pending', 'unknown'].includes(receipt.achAttempt?.state) ||
        recovery.resolvedAt !== null))
  )
    throw new Error('Invalid ACH review');
  return receipt;
}

/** Admin review does not move money, alter the ledger or initiate/retry any payment. */
export default function DistributorAchRecovery({
  distributorId,
  onRefresh,
  onFundingPendingChange,
  fundingDisabled,
}) {
  const t = useT();
  const [queue, setQueue] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [review, setReview] = useState(emptyReview);
  const [intent, setIntent] = useState(null);
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const intentRef = useRef(null);
  const busyRef = useRef(false);
  const originalSession = useRef(captureSession());
  const originalScope = useRef(shippingAccessScope());
  const current = () =>
    isSessionCurrent(originalSession.current) && originalScope.current === shippingAccessScope();
  const refreshCallback = useRef(onRefresh);
  refreshCallback.current = onRefresh;
  const { begin, version } = useShippingRequest(distributorId);
  const load = useCallback(
    async (nextPage = 1) => {
      if (busyRef.current) return;
      const request = begin();
      setLoading(true);
      setQueue(null);
      setReview(emptyReview);
      setError('');
      try {
        const value = unwrap(
          await distributorService.adminAchRecovery(distributorId, nextPage, {
            signal: request.signal,
          })
        );
        if (!request.isCurrent()) return;
        if (
          !Array.isArray(value?.data) ||
          value.data.length > 20 ||
          !safeCount(value.total) ||
          value.page !== nextPage ||
          value.limit !== 20 ||
          value.totalPages !== Math.ceil(value.total / 20) ||
          new Set(value.data.map((row) => row?._id)).size !== value.data.length
        )
          throw new Error('Invalid ACH queue');
        const data = value.data.map(readRecovery);
        if (nextPage > Math.max(1, value.totalPages)) {
          busyRef.current = false;
          await load(Math.max(1, value.totalPages));
          return;
        }
        setQueue({ ...value, data });
        setPage(nextPage);
        // A lost write remains locked. A queue revision cannot prove which request resolved it.
        if (intentRef.current)
          setError(
            'Review result is unconfirmed. Retry the same review request to recover its receipt.'
          );
      } catch {
        if (request.isCurrent())
          setError('ACH review records are unavailable. Reload before continuing.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [begin, distributorId]
  );
  useEffect(() => {
    busyRef.current = false;
    setBusy(false);
    intentRef.current = null;
    setIntent(null);
    setPendingCaseId(null);
    setReview(emptyReview);
    setNotice('');
    load(1);
  }, [load, version]);
  const submit = async (event) => {
    event.preventDefault();
    if (!current() || busyRef.current || loading || pendingCaseId) return;
    const row = queue?.data.find((value) => value._id === review.purchaseId);
    let captured = intentRef.current;
    if (!captured) {
      if (
        !row?.recovery.canResolve ||
        row.recovery.revision !== review.expectedRevision ||
        !review.confirmedReconciled ||
        review.reason.trim().length < 12 ||
        review.reason.trim().length > 300
      )
        return;
      const requestKey = window.crypto?.randomUUID?.();
      if (!requestKey) {
        setError('A secure request key is unavailable. Reload before reviewing.');
        return;
      }
      captured = {
        purchaseId: row._id,
        quote: row,
        body: {
          requestKey,
          expectedRevision: review.expectedRevision,
          reason: review.reason.trim(),
          confirmedReconciled: true,
        },
      };
      intentRef.current = captured;
      setIntent(captured);
    }
    busyRef.current = true;
    const request = begin();
    if (!request.isCurrent()) {
      busyRef.current = false;
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = readRecovery(
        unwrap(
          await distributorService.reconcileAchReturn(
            distributorId,
            captured.purchaseId,
            captured.body
          )
        )
      );
      if (!request.isCurrent()) return;
      const action = receipt.resolutionReceipt;
      if (
        receipt._id !== captured.purchaseId ||
        !samePurchaseQuote(receipt, captured.quote) ||
        receipt.status !== captured.quote.status ||
        receipt.bankEntryId !== captured.quote.bankEntryId ||
        receipt.creditedAt !== captured.quote.creditedAt ||
        action?.requestKey !== captured.body.requestKey ||
        action.expectedRevision !== captured.body.expectedRevision ||
        action.resolvedRevision !== captured.body.expectedRevision + 1 ||
        !date(action.resolvedAt) ||
        Date.parse(action.resolvedAt) < Date.parse(receipt.createdAt) ||
        receipt.recovery.revision < action.resolvedRevision ||
        (receipt.recovery.revision === action.resolvedRevision &&
          (receipt.recovery.canResolve || receipt.recovery.resolvedAt !== action.resolvedAt))
      )
        throw new Error('Invalid ACH review receipt');
      intentRef.current = null;
      setIntent(null);
      setReview(emptyReview);
      setNotice(
        receipt.recovery.canResolve
          ? 'Earlier reconciliation recovered. A newer return review remains open. Family spending remains paused. No units or money changed.'
          : receipt.recovery.blocked
            ? 'Reconciliation recorded. Family spending remains paused for another return review. No units or money changed.'
            : 'Reconciliation recorded. This review changed no units or money.'
      );
      refreshCallback.current?.();
      busyRef.current = false;
      setBusy(false);
      await load(page);
    } catch {
      if (request.isCurrent())
        setError(
          'Review result is unconfirmed. Retry the same review request to recover its receipt.'
        );
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  const reviewingReturned =
    (intent?.quote || queue?.data.find((row) => row._id === review.purchaseId))?.achAttempt
      ?.state === 'returned';
  return (
    <section className="me-distributor-admin-card space-y-3" aria-label={t('ACH payment review')}>
      <h2 className="text-lg font-semibold">{t('ACH payment review')}</h2>
      <p>
        {t(
          'Review merchant funding, refunds and returns. These confirmations do not credit units or move money; payment and accounting checks remain separate.'
        )}
      </p>
      <button
        type="button"
        disabled={busy || loading || Boolean(pendingCaseId)}
        onClick={() => load(page)}
      >
        {t('Refresh ACH reviews')}
      </button>
      {loading && <p role="status">{t('Loading ACH reviews...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {queue?.data.length === 0 && <p>{t('No ACH attempts need observation or return review.')}</p>}
      {queue && (
        <p>
          {t(
            'Showing {count} ACH records. Funding without a linked bank entry is not a unit credit.',
            { count: queue.total }
          )}
        </p>
      )}
      {queue?.data.map((row) => (
        <div key={row._id} className="rounded border p-3 text-sm">
          <p>
            {row.familyKey.replaceAll('_', ' ')} · {moneyMinor(row.amountCents)} USD · {row._id}
          </p>
          <p>{t(purchaseState(row))}</p>
          <p>{t(purchaseCreditMessage(row))}</p>
          {!intent && row.achAttempt?.state === 'funded' && (
            <DistributorMerchantFunding
              distributorId={distributorId}
              purchase={row}
              disabled={
                fundingDisabled ||
                busy ||
                loading ||
                Boolean(pendingCaseId && pendingCaseId !== row._id)
              }
              onPendingChange={(value) => {
                setPendingCaseId(value ? row._id : null);
                onFundingPendingChange?.(value);
              }}
              onRefresh={() => refreshCallback.current?.()}
            />
          )}
          {row.bankEntryId && (
            <p>
              {t('Bank entry ID')}: {row.bankEntryId}
            </p>
          )}
          <p>
            {t(
              row.recovery.blocked
                ? 'Family spending paused; affected unsent orders remain paused.'
                : row.recovery.resolvedAt
                  ? 'Return reconciliation recorded.'
                  : 'No return restriction recorded.'
            )}
          </p>
          {!intent && row.recovery.canViewSupportCase === true && (
            <DistributorPaymentSupportCase
              distributorId={distributorId}
              purchase={row}
              disabled={busy || loading || Boolean(pendingCaseId && pendingCaseId !== row._id)}
              onPendingChange={(value) => setPendingCaseId(value ? row._id : null)}
            />
          )}
          {row.recovery.canResolve && (
            <button
              type="button"
              disabled={busy || Boolean(intent) || Boolean(pendingCaseId)}
              onClick={() => {
                if (!current()) return;
                const request = begin();
                if (request.isCurrent())
                  setReview({
                    ...emptyReview,
                    purchaseId: row._id,
                    expectedRevision: row.recovery.revision,
                  });
              }}
            >
              {t(
                row.achAttempt?.state === 'returned'
                  ? 'Review returned purchase {id}'
                  : 'Review ACH payment {id}',
                { id: row._id }
              )}
            </button>
          )}
        </div>
      ))}
      {(review.purchaseId || intent) && (
        <form onSubmit={submit} className="space-y-3">
          <p>
            {t('Purchase ID')}: {intent?.purchaseId || review.purchaseId}
          </p>
          <fieldset disabled={busy || Boolean(intent)} className="space-y-3">
            <label className="block">
              {t('Reconciliation reason')}
              <textarea
                className="block w-full rounded border p-2"
                maxLength={300}
                value={review.reason}
                onChange={(event) =>
                  setReview((value) => ({
                    ...value,
                    reason: event.target.value,
                    confirmedReconciled: false,
                  }))
                }
              />
            </label>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={review.confirmedReconciled}
                onChange={(event) =>
                  setReview((value) => ({ ...value, confirmedReconciled: event.target.checked }))
                }
              />
              {t(
                reviewingReturned
                  ? 'I reviewed bank, payment and accounting reconciliation and authorize resolving this returned purchase.'
                  : 'I reviewed bank, payment and accounting reconciliation and authorize resolving this payment review.'
              )}
            </label>
          </fieldset>
          <button
            type="submit"
            disabled={
              busy ||
              loading ||
              Boolean(pendingCaseId) ||
              (!intent && (!review.confirmedReconciled || review.reason.trim().length < 12))
            }
          >
            {t(
              intent
                ? 'Recover same reconciliation request'
                : reviewingReturned
                  ? 'Record return reconciliation'
                  : 'Record ACH payment reconciliation'
            )}
          </button>
        </form>
      )}
      {queue?.totalPages > 1 && (
        <nav aria-label={t('ACH review pages')} className="flex gap-3">
          <button
            type="button"
            disabled={busy || loading || Boolean(intent) || Boolean(pendingCaseId) || page <= 1}
            onClick={() => load(page - 1)}
          >
            {t('Previous')}
          </button>
          <span>{t('Page {page} of {pages}', { page, pages: queue.totalPages })}</span>
          <button
            type="button"
            disabled={
              busy ||
              loading ||
              Boolean(intent) ||
              Boolean(pendingCaseId) ||
              page >= queue.totalPages
            }
            onClick={() => load(page + 1)}
          >
            {t('Next')}
          </button>
        </nav>
      )}
    </section>
  );
}

export function DistributorAchMonitorHealth() {
  const t = useT();
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const { begin, version } = useShippingRequest('ach-monitor-health');
  const load = useCallback(async () => {
    const request = begin();
    setHealth(null);
    setError('');
    try {
      const value = unwrap(
        await distributorService.adminAchMonitorHealth({ signal: request.signal })
      );
      if (!request.isCurrent()) return;
      if (
        typeof value?.enabled !== 'boolean' ||
        typeof value.ready !== 'boolean' ||
        !(
          value.owner === null ||
          (typeof value.owner === 'string' &&
            value.owner.trim().length > 0 &&
            value.owner.length <= 120)
        ) ||
        !(
          value.intervalMinutes === null ||
          (Number.isFinite(value.intervalMinutes) && value.intervalMinutes >= 0)
        ) ||
        ![
          'off',
          'unconfigured',
          'unverified',
          'stalled',
          'failed',
          'attention',
          'healthy',
        ].includes(value.status) ||
        !safeCount(value.backlogCount) ||
        !safeCount(value.manualReviewCount) ||
        !['lastSuccessAt', 'lastErrorAt', 'lastRunAt', 'oldestBacklogAt'].every(
          (key) => value[key] === null || date(value[key])
        ) ||
        (value.ready &&
          (!value.enabled ||
            value.status !== 'healthy' ||
            !value.owner ||
            !date(value.lastSuccessAt)))
      )
        throw new Error('Invalid monitor health');
      setHealth(value);
    } catch {
      if (request.isCurrent())
        setError('Payment monitor health is unavailable. Readiness is unconfirmed.');
    }
  }, [begin]);
  useEffect(() => {
    load();
  }, [load, version]);
  const labels = {
    off: 'Off',
    unconfigured: 'Owner or interval not configured',
    unverified: 'No successful monitor run verified',
    stalled: 'Monitor stalled',
    failed: 'Monitor failed',
    attention: 'Manual review required',
    healthy: 'Monitor healthy',
  };
  return (
    <section
      className="me-distributor-admin-card me-bank-monitor space-y-3"
      aria-label={t('Purchase payment monitor health')}
    >
      <header className="me-bank-monitor-header">
        <h2>{t('Purchase payment monitor health')}</h2>
        <button type="button" onClick={load} aria-label={t('Refresh payment monitor health')}>
          {t('Refresh')}
        </button>
      </header>
      {error && <p role="alert">{t(error)}</p>}
      {!health && !error && <p role="status">{t('Loading payment monitor health...')}</p>}
      {health && (
        <>
          <p
            role="status"
            className={`me-bank-monitor-status ${health.ready ? 'is-ready' : 'is-blocked'}`}
          >
            {t(labels[health.status])} · {t(health.ready ? 'Monitor ready' : 'Activation blocked')}
          </p>
          {(health.backlogCount > 0 || health.manualReviewCount > 0) && (
            <p>
              {t('Observation backlog')}: {health.backlogCount} · {t('Payment observation reviews')}
              : {health.manualReviewCount}
            </p>
          )}
          {health.lastSuccessAt && (
            <p>
              {t('Last successful observation')}: {new Date(health.lastSuccessAt).toLocaleString()}
            </p>
          )}
          <details className="me-bank-monitor-details">
            <summary>{t('Details')}</summary>
            <p>
              {t('Operational owner')}: {health.owner || t('Unassigned')}
            </p>
            {health.intervalMinutes !== null && (
              <p>
                {t('Monitor interval (minutes)')}: {health.intervalMinutes ?? t('Unavailable')}
              </p>
            )}
            {['lastRunAt', 'lastErrorAt', 'oldestBacklogAt']
              .filter((key) => health[key])
              .map((key) => (
                <p key={key}>
                  {t(
                    {
                      lastRunAt: 'Last monitor run',
                      lastSuccessAt: 'Last successful observation',
                      lastErrorAt: 'Last monitor error',
                      oldestBacklogAt: 'Oldest pending observation',
                    }[key]
                  )}
                  : {health[key] ? new Date(health[key]).toLocaleString() : t('Unavailable')}
                </p>
              ))}
          </details>
        </>
      )}
    </section>
  );
}
